import type { TextConfig } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import { PresenceBadge } from "@fluentui/react-components";
import { Html, Text } from "@react-three/drei";
import { Euler, Vector3 } from "three";

import { useShowLabelsButton } from "../../hooks/use-show-labels-button.js";

const DEFAULT_FONT_SIZE = 0.35;
const DEFAULT_COLOR = "#7B706F";
const DEFAULT_FONT_WEIGHT = "bold";
const DEFAULT_LETTER_SPACING = 0.15;
const BADGE_OFFSET_Y = 0.45;
const BADGE_OFFSET_X = -0.2;

interface GeneralLabelProps {
  value: string;
  hovered: boolean;
  config: TextConfig;
  status?: UserAvailability | null;
}

export const GeneralLabel = ({
  value,
  config,
  hovered = false,
  status,
}: GeneralLabelProps) => {
  const showAllLabels = useShowLabelsButton();
  const visible = hovered || showAllLabels;

  if (!visible) {
    return null;
  }

  const posX = config.position[0] ?? 0;
  const posY = config.position[1] ?? 0;
  const posZ = config.position[2] ?? 0;
  const presenceStatus = status ?? undefined;

  return (
    <group>
      <Text
        anchorX="center"
        anchorY="middle"
        color={config.color ?? DEFAULT_COLOR}
        fontSize={config.size ?? DEFAULT_FONT_SIZE}
        fontWeight={config.fontWeight ?? DEFAULT_FONT_WEIGHT}
        letterSpacing={config.letterSpacing ?? DEFAULT_LETTER_SPACING}
        position={new Vector3(posX, posY, posZ)}
        renderOrder={1}
        rotation={
          new Euler(
            config.rotation[0] ?? 0,
            config.rotation[1] ?? 0,
            config.rotation[2] ?? 0
          )
        }
      >
        {value}
      </Text>
      {presenceStatus && (
        <Html
          center
          position={
            new Vector3(posX, posY + BADGE_OFFSET_Y, posZ + BADGE_OFFSET_X)
          }
          style={{ pointerEvents: "none" }}
        >
          <PresenceBadge size="large" status={presenceStatus} />
        </Html>
      )}
    </group>
  );
};
